import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, DemoBadge } from "@/components/Badges";
import { fmtDateTime, FUNDING_TYPES } from "@/lib/format";
import { VerificationQueue } from "@/components/VerificationQueue";
import { Partnerships } from "@/components/admin/Partnerships";
import { HubAdmin } from "@/components/admin/HubAdmin";
import { I2IAdmin } from "@/components/admin/I2IAdmin";
import { FeedbackAdmin } from "@/components/admin/FeedbackAdmin";

const CONFIG = {
  opportunities: [["title", "Title"], ["funder", "Funder"], ["funding_type", "Type"], ["close_date", "Close date"], ["award_max", "Max award", "number"], ["application_url", "Official URL"]],
  sources: [["name", "Name"], ["url", "URL"], ["method", "Method"], ["active", "Active", "bool"]],
  pricing: [["name", "Name"], ["category", "Category"], ["amount", "Amount (£)", "number"], ["annual_amount", "Yearly price (£, plans only)", "number"], ["unit", "Unit"], ["note", "Note"]],
  advisers: [["name", "Name"], ["specialism", "Specialism"], ["regulator", "Regulator / registration"], ["email", "Email"], ["active", "Active", "bool"]],
  templates: [["name", "Name"], ["kind", "Kind"], ["submission_route", "Route"], ["description", "Description"]],
};
const CREATABLE = new Set(["opportunities", "pricing", "advisers", "sources"]);

const Cell = ({ type, value, onChange }) => type === "bool"
  ? <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[#0D9488]" />
  : <input className="input py-1 text-xs" type={type === "number" ? "number" : "text"} value={value ?? ""} onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} />;

const Row = ({ coll, row, fields, reload }) => {
  const [r, setR] = useState(row);
  const save = async () => {
    try { await api.put(`/admin/${coll}/${row.id}`, Object.fromEntries(fields.map(([k]) => [k, r[k]]))); toast.success("Saved"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr className="border-t border-slate-100 align-top" data-testid={`admin-row-${row.id}`}>
      {fields.map(([k, , t]) => <td key={k} className="p-1.5"><Cell type={t} value={r[k]} onChange={(v) => setR({ ...r, [k]: v })} /></td>)}
      <td className="whitespace-nowrap p-1.5 text-xs">
        {row.is_demo && <DemoBadge text="Demo" />}
        {coll === "opportunities" && row.verification?.status && <span className="caption ml-1">{row.verification.status.replace("_", " ")}</span>}
        <button onClick={save} data-testid={`admin-save-${row.id}`} className="ml-2 text-[#0D9488] hover:underline">Save</button>
        {["pricing", "advisers", "opportunities"].includes(coll) && <button onClick={() => api.delete(`/admin/${coll}/${row.id}`).then(reload)} className="ml-2 text-red-600 hover:underline">Delete</button>}
      </td>
    </tr>
  );
};

const Table = ({ coll }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const fields = CONFIG[coll];
  const load = () => api.get(`/admin/${coll}`).then(({ data }) => setRows(data));
  useEffect(() => {
    load();
  }, [coll]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    const base = coll === "opportunities" ? { title: "New opportunity", funder: "", funding_type: "grant", source_name: "Added by admin" } : coll === "pricing" ? { name: "New item", category: "platform" } : { name: "New", active: false };
    try { await api.post(`/admin/${coll}`, base); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = rows.filter((r) => !q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase())).slice(0, 100);
  return (
    <div className="card overflow-x-auto p-4" data-testid={`admin-table-${coll}`}>
      <div className="mb-3 flex gap-2">
        <input aria-label="Filter" className="input py-1.5 text-sm" placeholder="Filter…" value={q} onChange={(e) => setQ(e.target.value)} />
        {CREATABLE.has(coll) && <button onClick={add} data-testid={`admin-add-${coll}`} className="btn-teal btn-sm">Add</button>}
      </div>
      {coll === "opportunities" && <p className="mb-2 text-xs text-slate-500">Funding types: {Object.keys(FUNDING_TYPES).join(", ")}. Showing first 100 of {rows.length}.</p>}
      <table className="w-full min-w-[800px] text-sm">
        <thead><tr className="text-left text-xs text-slate-500">{fields.map(([k, l]) => <th key={k} className="p-1.5">{l}</th>)}<th /></tr></thead>
        <tbody>{shown.map((r) => <Row key={r.id} coll={coll} row={r} fields={fields} reload={load} />)}</tbody>
      </table>
    </div>
  );
};

const Support = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/support").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <ul className="space-y-2" data-testid="admin-support-list">
      {rows.map((s) => (
        <li key={s.id} className="card p-4 text-sm">
          <div className="flex flex-wrap justify-between gap-2"><b>{s.subject || s.type}</b>
            <select aria-label="Status" value={s.status} onChange={(e) => api.put(`/admin/support/${s.id}`, { status: e.target.value }).then(load)} className="input w-36 py-1 text-xs"><option value="open">Open</option><option value="in_progress">In progress</option><option value="closed">Closed</option></select>
          </div>
          <p className="text-xs text-slate-500">{s.type}{s.confidential ? " · CONFIDENTIAL" : ""} · {s.name} &lt;{s.email}&gt; · {fmtDateTime(s.at)}</p>
          <p className="mt-2 whitespace-pre-line">{s.message}</p>
        </li>
      ))}
    </ul>
  );
};

const Overview = () => {
  const { setSettings } = useAuth();
  const [st, setSt] = useState(null);
  const [settings, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => { api.get("/admin-actions/status").then(({ data }) => setSt(data)); api.get("/admin/settings").then(({ data }) => setS(data)); };
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const saveSettings = async () => {
    try { const { data } = await api.put("/admin/settings", settings); setSettings(data); toast.success("Settings saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const refresh = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin-actions/refresh"); toast.success(`Find a Grant: ${data.status} (${data.added} new, ${data.updated} updated)`); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!st || !settings) return <p className="text-sm text-slate-500">Loading…</p>;
  return (
    <div className="space-y-6">
      <section className="card grid gap-4 p-6 sm:grid-cols-3">
        <div><label className="label" htmlFor="pn">Platform name</label><input id="pn" data-testid="admin-platform-name-input" className="input" value={settings.platform_name} onChange={(e) => setS({ ...settings, platform_name: e.target.value })} /></div>
        {[["support_email", "Support email (public, receives enquiries)"], ["support_phone", "Support phone"], ["support_address", "Contact address"], ["company_name", "Legal company name"], ["company_number", "Company number"], ["company_address", "Registered office address"]].map(([k, l]) => (
          <div key={k}><label className="label" htmlFor={`set-${k}`}>{l}</label><input id={`set-${k}`} data-testid={`admin-${k}-input`} className="input" value={settings[k] || ""} onChange={(e) => setS({ ...settings, [k]: e.target.value })} /></div>
        ))}
        <label className="flex items-center gap-2 self-end text-sm"><input type="checkbox" data-testid="admin-require-partnership" checked={settings.require_partnership !== false} onChange={(e) => setS({ ...settings, require_partnership: e.target.checked })} />Require platform access to prepare applications</label>
        <label className="flex items-center gap-2 self-end text-sm"><input type="checkbox" data-testid="admin-member-complimentary" checked={!!settings.member_complimentary_active} onChange={(e) => setS({ ...settings, member_complimentary_active: e.target.checked })} />Activate complimentary platform access for verified Hub members (proposed)</label>
        <label className="flex items-center gap-2 self-end text-sm"><input type="checkbox" data-testid="admin-demo-checkout" checked={!!settings.demo_checkout} onChange={(e) => setS({ ...settings, demo_checkout: e.target.checked })} />Testing checkout: show the payment step but let people continue without paying</label>
        <label className="flex items-center gap-2 self-end text-sm text-slate-400"><input type="checkbox" disabled checked={false} readOnly />Sponsor module (future, disabled)</label>
        <label className="flex items-center gap-2 self-end text-sm text-slate-400"><input type="checkbox" disabled checked={false} readOnly />Selected funding partner unlock (future, disabled)</label>
        <button onClick={saveSettings} data-testid="admin-save-settings" className="btn-teal btn-sm self-end">Save settings</button>
      </section>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">{Object.entries(st.counts).map(([k, v]) => <div key={k} className="card p-4"><p className="caption">{k.replace(/_/g, " ")}</p><p className="font-display text-xl font-bold" data-testid={`admin-count-${k}`}>{v}</p></div>)}</div>
      <section className="card p-6" data-testid="integration-status">
        <div className="flex items-center justify-between"><h2 className="font-semibold text-[#0B192C]">Integration status</h2><button onClick={refresh} disabled={busy} data-testid="admin-refresh-button" className="btn-ghost btn-sm"><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />Refresh Find a Grant now</button></div>
        <ul className="mt-3 divide-y divide-slate-100 text-sm">{st.integrations.map((i) => <li key={i.name} className="py-2"><b>{i.name}</b> · <span className="caption">{i.status}</span><p className="truncate text-xs text-slate-500">{i.note}</p></li>)}</ul>
      </section>
    </div>
  );
};

const TABS = [["overview", "Overview"], ["hub", "Westmont Hub"], ["programme", "i2i, panels & documents"], ["feedback", "Tester feedback"], ["partnerships", "Incubation enquiries (legacy)"], ["verification", "Verification"], ["opportunities", "Funding opportunities"], ["sources", "Funding sources"], ["templates", "Templates"], ["advisers", "Advisers"], ["pricing", "Pricing"], ["support", "Support requests"]];

export default function Admin() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="fade-up">
      <PageHeader title="Admin console" sub="Manage funding data, templates, advisers, pricing, support and integrations." />
      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-[#0D9488]" : "border-transparent text-slate-500"}`}>{l}</button>)}
      </div>
      {tab === "overview" && <Overview />}
      {tab === "support" && <Support />}
      {tab === "verification" && <VerificationQueue />}
      {tab === "partnerships" && <Partnerships />}
      {tab === "hub" && <HubAdmin />}
      {tab === "programme" && <I2IAdmin />}
      {tab === "feedback" && <FeedbackAdmin />}
      {CONFIG[tab] && <Table key={tab} coll={tab} />}
    </div>
  );
}
